import { gql } from '@urql/core';

const SUBSCHEMA_FRAGMENT = gql`
  fragment subschemaFragment on TTSchemaEntityDto {
    id
    name
    titlePlural
    titleSingular
    type
    fields {
      id
      name
      displayName
      type
      readOnly
      hasMultipleValues
      typeDefinition
      visibility
      sortOrder
      required
      TTSchema {
        id
      }
      typeTTSchema {
        id
      }
    }
  }
`;

const REVISION_SCHEMA_FRAGMENT = gql`
  fragment revisionSchemaFragment on PageRevisionSchemaEntityDto {
    id
    sortOrder
    anchorId
    label
    campaign
    schemaBlockId
    hideBlockForCampaignPlaceholders
    inHeader
    inFooter
    properties {
      fieldId
      name
      value
      isDynamic
      dynamicReference
      valueReferences {
        referenceId
      }
    }
    ttschema(draftMode: $draftMode) {
      id
      name
      titlePlural
      titleSingular
      type
      deletedAt
      thumbnailImageUrls {
        desktop
        tablet
        mobile
      }
      fields {
        id
        name
        type
        readOnly
        hasMultipleValues
        displayName
        typeDefinition
        visibility
        sortOrder
        required
        TTSchema {
          id
        }
        typeTTSchema {
          id
        }
      }
      subSchemas {
        ...subschemaFragment
      }
    }
  }
  ${SUBSCHEMA_FRAGMENT}
`;

export const REVISION_FRAGMENT = gql`
  fragment revisionFragment on PageRevisionEntityDto {
    id
    createdAt
    references
    type
    resolvedReferences {
      dynamicReference
      value
    }
    schema(draftMode: $draftMode) {
      ...revisionSchemaFragment
      template {
        id
        name
        deletedAt
        publishedRevision {
          id
          createdAt
          resolvedReferences {
            dynamicReference
            value
          }
          schema {
            ...revisionSchemaFragment
          }
        }
      }
    }
  }
  ${REVISION_SCHEMA_FRAGMENT}
`;

export const TT_SCHEMA_FRAGMENT = gql`
  fragment ttschemaFragment on TTSchemaEntityDto {
    id
    name
    titlePlural
    titleSingular
    type
    thumbnailImageUrls {
      desktop
      tablet
      mobile
    }
    visibility
    fields {
      id
      name
      helpText
      description
      displayName
      hasMultipleValues
      type
      sortOrder
      typeDefinition
      visibility
      required
      TTSchema {
        id
      }
      typeTTSchema {
        id
      }
    }
    subSchemas {
      ...subschemaFragment
    }
  }
  ${SUBSCHEMA_FRAGMENT}
`;

export const GET_PAGE = gql`
  query Page($id: Int!, $draftMode: Boolean = true) {
    page(id: $id) {
      id
      name
      urlSegment
      urlPath
      metaTitle
      metaDescription
      allowCrawling
      templateId
      scheduledAt
      site {
        id
        primaryDomain {
          id
          domainName
          ssl
          path
        }
        brand {
          organisation {
            id
          }
        }
        customScriptHead
        customScriptBody
        config {
          version
        }
      }
      draftRevision {
        ...revisionFragment
      }
      publishedRevision {
        id
      }
      draftCustomFields {
        id
      }
      publishedCustomFields {
        id
      }
      parent {
        id
      }
      pageType {
        id
        name
        titlePlural
        titleSingular
        type
        fields {
          id
          name
          displayName
          type
          readOnly
          hasMultipleValues
          typeDefinition
          visibility
          sortOrder
          required
          TTSchema {
            id
          }
          typeTTSchema {
            id
          }
        }
        subSchemas {
          ...subschemaFragment
        }
      }
    }
  }
  ${REVISION_FRAGMENT}
`;

export const GET_PAGE_FOR_PUBLISH_VALIDATION = gql`
  query PageForPublishValidation($id: Int!, $draftMode: Boolean = true) {
    page(id: $id) {
      id
      name
      template {
        id
      }
      pageType {
        id
        name
        titlePlural
        titleSingular
        type
        fields {
          id
          name
          displayName
          type
          readOnly
          hasMultipleValues
          typeDefinition
          visibility
          sortOrder
          required
          TTSchema {
            id
          }
          typeTTSchema {
            id
          }
        }
        subSchemas {
          ...subschemaFragment
        }
      }
      site {
        id
      }
      parent {
        id
      }
      draftRevision {
        ...revisionFragment
      }
      draftCustomFields {
        ...revisionFragment
      }
    }
  }
  ${REVISION_FRAGMENT}
`;

export const GET_ALL_SITE_PAGE_IDS = gql`
  query AllSitePageIds(
    $siteId: Int!
    $pageName: String
    $schemaTypeId: [Int!]
    $parentId: Int
    $role: Role
    $requirePageTypeId: Boolean = false
  ) {
    pages(
      siteId: $siteId
      pageName: $pageName
      schemaTypeId: $schemaTypeId
      parentId: $parentId
      role: $role
      requirePageTypeId: $requirePageTypeId
    ) {
      items {
        id
      }
      total
    }
  }
`;

export const GET_PAGES = gql`
  query SitePages(
    $pageIds: [Int!]
    $siteId: Int!
    $parentId: Int
    $offset: Int
    $limit: Int
    $pageName: String
    $order: PageOrder
    $orderDirection: PageOrderDirection
    $schemaTypeId: [Int!]
    $requirePageTypeId: Boolean = false
    $role: Role
  ) {
    pages(
      pageIds: $pageIds
      siteId: $siteId
      parentId: $parentId
      offset: $offset
      limit: $limit
      pageName: $pageName
      order: $order
      orderDirection: $orderDirection
      schemaTypeId: $schemaTypeId
      requirePageTypeId: $requirePageTypeId
      role: $role
    ) {
      items {
        id
        name
        urlSegment
        metaTitle
        metaDescription
        allowCrawling
        templateId
        urlPath
        scheduledAt
        pageType {
          name
          titleSingular
        }
        draftRevision {
          id
          createdAt
        }
        publishedRevision {
          id
        }
        draftCustomFields {
          id
        }
        publishedCustomFields {
          id
        }
        parent {
          id
        }
        campaignParent {
          id
        }
        childrenCount
      }
      total
    }
  }
`;

export const GET_PAGE_SELECTOR_PAGES = gql`
  query PageSelectorSitePages($pageIds: [Int!], $siteId: Int!, $limit: Int) {
    pages(pageIds: $pageIds, siteId: $siteId, limit: $limit) {
      items {
        id
        name
        urlSegment
        pageType {
          titleSingular
        }
      }
      total
    }
  }
`;

export const LIST_PAGES = gql`
  query LIST_PAGES($siteId: Int!, $parentId: Int) {
    pages(siteId: $siteId, parentId: $parentId) {
      items {
        id
        name
        childrenCount
      }
    }
  }
`;

export const GET_PARENT_PAGE = gql`
  query ParentPage($id: Int!) {
    page(id: $id) {
      id
      name
      urlSegment
      urlPath
      pageType {
        id
        name
        titleSingular
      }
      parent {
        id
        name
        urlPath
        parent {
          id
          name
          urlPath
        }
      }
    }
  }
`;

export const GET_PARENT_PAGE_OPTIONS = gql`
  query PaginatedPageQuery(
    $parentId: Int
    $siteId: Int!
    $limit: Int
    $pageId: Int
    $pageName: String
    $exclude: [Int!]
    $excludeRecursive: Boolean
  ) {
    pages(
      siteId: $siteId
      parentId: $parentId
      pageId: $pageId
      pageName: $pageName
      limit: $limit
      exclude: $exclude
      excludeRecursive: $excludeRecursive
    ) {
      items {
        id
        name
        urlPath
        urlSegment
      }
    }
  }
`;

export const VALIDATE_PAGE_CREATE = gql`
  query ValidatePageCreate($input: ValidationInputDto!) {
    validateCreatePageDto(validationInput: $input) {
      hasErrors
      errors {
        property
        constraints
      }
    }
  }
`;

export const CREATE_PAGE = gql`
  mutation CreatePage($page: CreatePageDto!) {
    createPage(page: $page) {
      id
    }
  }
`;

export const GET_PAGE_OPTIONS = gql`
  query PageOptions($siteId: Int!, $limit: Int, $pageName: String) {
    pages(siteId: $siteId, pageName: $pageName, limit: $limit) {
      items {
        id
        name
        urlPath
        urlSegment
      }
    }
  }
`;

export const GET_PAGE_TYPE = gql`
  query GetPageWithPageType($id: Int!) {
    page(id: $id) {
      id
      name
      pageType {
        id
        name
      }
    }
  }
`;

export const PUBLISH_PAGE = gql`
  mutation PublishPage($pageId: Int!) {
    publishPage(pageId: $pageId) {
      success
    }
  }
`;

export const UNPUBLISH_PAGE = gql`
  mutation UnpublishPage($pageId: Int!) {
    unpublishPage(pageId: $pageId) {
      success
    }
  }
`;

export const UPDATE_PAGE = gql`
  mutation UpdatePage($page: UpdatePageDto!) {
    updatePage(page: $page) {
      id
    }
  }
`;

export const UPDATE_PAGE_SEO_SETTINGS = gql`
  mutation updatePageSEOSettings($page: UpdatePageSEOSettingsDto!) {
    updatePageSEOSettings(page: $page) {
      id
    }
  }
`;

export const GET_PAGE_CUSTOMFIELDS = gql`
  query PageCustomFields($id: Int!, $draftMode: Boolean = true) {
    page(id: $id) {
      id
      name
      urlPath
      templateId
      scheduledAt
      template {
        publishedRevision {
          id
        }
      }
      publishedCustomFields {
        ...revisionFragment
      }
      draftCustomFields {
        ...revisionFragment
      }
      draftRevision {
        id
      }
      publishedRevision {
        id
      }
      parent {
        id
      }
      site {
        id
        primaryDomain {
          id
          domainName
          ssl
          path
        }
        customScriptHead
        customScriptBody
      }
      pageType {
        id
        name
        titlePlural
        titleSingular
        type
        fields {
          id
          name
          type
          readOnly
          hasMultipleValues
          displayName
          typeDefinition
          visibility
          sortOrder
          required
          TTSchema {
            id
          }
          typeTTSchema {
            id
          }
        }
        subSchemas {
          id
          name
          titlePlural
          titleSingular
          type
          fields {
            id
            name
            displayName
            type
            readOnly
            hasMultipleValues
            typeDefinition
            visibility
            sortOrder
            required
            TTSchema {
              id
            }
            typeTTSchema {
              id
            }
          }
        }
      }
    }
  }
  ${REVISION_FRAGMENT}
`;

export const GET_PAGE_CUSTOMFIELDS_PREVIEW = gql`
  query PageCustomFieldsPreview(
    $pageData: PageRevisionReferencesWithRevisionSchemas!
    $draftMode: Boolean = true
  ) {
    pageRevisionFromPageData(pageData: $pageData) {
      ...revisionFragment
    }
  }
  ${REVISION_FRAGMENT}
`;

export const DISCARD_PAGE_DRAFT = gql`
  mutation DiscardPageDraft($id: Int!, $draftMode: Boolean = false) {
    discardPageDraft(id: $id) {
      id
      publishedRevision {
        ...revisionFragment
      }
    }
  }
  ${REVISION_FRAGMENT}
`;

export const DISCARD_TEMPLATE_DRAFT = gql`
  mutation DiscardTemplateDraft($id: Int!, $draftMode: Boolean = false) {
    discardTemplateDraft(id: $id) {
      id
      publishedRevision {
        ...revisionFragment
      }
    }
  }
  ${REVISION_FRAGMENT}
`;

export const GET_PAGE_FOR_SETTINGS = gql`
  query PageForSettings($pageId: Int!) {
    page(id: $pageId) {
      id
      name
      urlSegment
      urlPath
      allowCrawling
      excludeFromSitemap
      metaTitle
      metaDescription
      metaTags
      templateId
      parent {
        id
        name
        urlPath
      }
      pageType {
        id
        name
        titlePlural
        titleSingular
      }
    }
  }
`;

export const VALIDATE_PAGE_UPDATE = gql`
  query ValidatePageUpdateSettings($input: ValidationInputDto!) {
    validateUpdatePageDto(validationInput: $input) {
      hasErrors
      errors {
        property
        constraints
      }
    }
  }
`;

export const GET_PAGE_PARENT = gql`
  query GetPageParent($id: Int!) {
    page(id: $id) {
      id
      parent {
        id
      }
    }
  }
`;

export const DELETE_PAGE = gql`
  mutation DeletePage($id: Int!) {
    deletePage(id: $id) {
      id
    }
  }
`;

export const CANCEL_SCHEDULED_PUBLISH = gql`
  mutation CancelScheduledPublish($id: Int!) {
    cancelScheduledPublish(id: $id) {
      id
    }
  }
`;

export const DUPLICATE_PAGE = gql`
  mutation DuplicatePage($id: Int!) {
    duplicatePage(id: $id) {
      id
    }
  }
`;

export const CHECK_PAGE_FOR_CAMPAIGN_USAGES = gql`
  query CheckPageUsagesInCampaigns($pageId: Int!) {
    checkPageUsagesInCampaigns(pageId: $pageId) {
      id
      name
    }
  }
`;

export const RESOLVE_REFERENCES = gql`
  query ResolveReferences($pageId: Int, $references: [UnresolvedReferenceDto!]!) {
    resolveReferences(pageId: $pageId, references: $references) {
      dynamicReference
      value
    }
  }
`;

export const GET_PAGE_TYPES = gql`
  query GetPageTypes($pageIds: [Int!]!) {
    getPageTypes(pageIds: $pageIds) {
      id
      name
      titleSingular
    }
  }
`;

export const GET_PAGE_TEMPLATES = gql`
  query GetPageTemplates($pageIds: [Int!]!) {
    getPageTemplates(pageIds: $pageIds) {
      id
      name
      type
    }
  }
`;

export const CLONE_PAGES_MUTATION = gql`
  mutation ClonePages($clonePagesInput: ClonePagesInput!) {
    clonePages(clonePagesInput: $clonePagesInput) {
      success
    }
  }
`;

export const IMPORT_PAGES_FROM_JSON_MUTATION = gql`
  mutation ImportPagesFromJson($importInput: ImportPagesFromJsonInput!) {
    importPagesFromJson(importInput: $importInput) {
      success
      errorMessage
      pagesCreated {
        id
        name
        urlPath
      }
    }
  }
`;
export const GET_PAGE_OWNERSHIP = gql`
  query GetPageOwnership($id: Int!) {
    page(id: $id) {
      id
      site {
        id
      }
      parent {
        id
      }
    }
  }
`;
